import {
  Component,
  Listen,
  h,
  Fragment,
  State,
  ComponentInterface,
} from '@stencil/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { PushNotifications } from '@capacitor/push-notifications';
import { Clipboard } from '@capacitor/clipboard';
import { Device } from '@capacitor/device';
import { SignUpAttribution } from '@og-shared/types';

import { initFirebase, signInWithToken } from '../../global/firebase';
import {
  AnalyticsEvent,
  initAnalytics,
  trackAnalyticsEvent,
} from '../../services/analytics';
import { initPolyglot, translate } from '../../global/translate';
import { groupDemoExit } from '../../store/actions/group';
import {
  loadStoredPreferences,
  onUiStore,
  uiState,
} from '../../store/ui.store';
import { groupState, onGroupStore } from '../../store/group.store';
import {
  authState,
  loadStoredAuthState,
  onAuthStore,
  waitForAuthLoaded,
  waitForAuthenticated,
} from '../../store/auth.store';
import { onBudgetStore } from '../../store/budgets.store';
import { onTransactionsStore } from '../../store/transactions.store';
import { onBudgetViewStore } from '../../store/budget-view.store';
import {
  getStorageKey,
  removeStorageKey,
  setStorageKey,
  StorageKeys,
} from '../../global/storage';
import { promptRefresh } from '../../global/update-available-prompt';
import { deepLinkInApp } from '../../global/route-in-app';
import { initIntercom } from '../../services/intercom';
import { openMessenger } from '../../services/messaging';
import { getUrlParameters } from '../../global/get-url-parameters';
import { saveAttributionIfNotPresent } from '../../global/attribution-utils';
import { getAndSetPartnerData } from '../../global/get-partner-data';
import { asaAuthIfParams } from '../../global/asa-utils';
import { sentryInit } from '../../services/sentry';
import { environment } from '../../global/environment';
import { presentOpenAppPrompt } from '../pages/open-app-prompt/present-open-app-prompt';
import { renderRouter } from './render-router';
import { onRulesStore } from '../../store/rules.store';

@Component({
  tag: 'og-app',
  styleUrl: 'og-app.scss',
})
export class OgApp implements ComponentInterface {
  @State() keyboardShown = false;

  async componentWillLoad() {
    sentryInit();
    initAnalytics();

    const validLocale = await getStorageKey(StorageKeys.og_locale);
    await loadStoredAuthState();
    await loadStoredPreferences();
    await initPolyglot(validLocale);
    uiState.locale = validLocale;
    await initFirebase();
    await this.handleDeferredDeepLinks();
    await this.getPartnerDataIfPartnerId();
    const params = getUrlParameters({
      url: location.search,
    });
    await asaAuthIfParams(params);
    await this.autoSignInIfToken();
    this.handleShowOpenAppPrompt();
    this.debugStoreLogs();
    this.setupKeyboard();
  }

  componentDidLoad() {
    this.hideLoading();
    this.handleDeepLinks();
    this.handlePushNotifications();
    this.saveAttribution();
    initIntercom();
  }

  @Listen('swUpdate')
  async onSWUpdate() {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration || !registration.waiting) {
      // If there is no registration, this is the first service
      // worker to be installed. registration.waiting is the one
      // waiting to be activated.
      return;
    }
    promptRefresh();
  }

  private handleDeepLinks() {
    try {
      App.addListener('appUrlOpen', data => this.openURL(data));
    } catch (err) {
      console.info(err);
    }
  }

  private async handleDeferredDeepLinks() {
    if (!Capacitor.isNativePlatform()) return;
    const firstLaunch = await getStorageKey(StorageKeys.og_first_launch);
    if (!firstLaunch) return;
    setStorageKey(StorageKeys.og_first_launch, false);
    try {
      const res = await Clipboard.read();
      return deepLinkInApp(res.value);
    } catch (err) {
      console.log('error reading clipboard', err);
    }
  }

  private async handleShowOpenAppPrompt() {
    if (!uiState.features.open_native_app_prompt) return;
    const native = Capacitor.isNativePlatform();
    if (native) return false;
    const device = await Device.getInfo();

    const operatingSystem = device.operatingSystem;
    if (operatingSystem !== 'android' && operatingSystem !== 'ios') return;
    presentOpenAppPrompt(operatingSystem);
  }

  private handlePushNotifications() {
    const isAvailable = Capacitor.isPluginAvailable('PushNotifications');
    if (!isAvailable) return;
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      notification => {
        const data = notification.notification?.data;
        trackAnalyticsEvent(AnalyticsEvent.push_notification_opened, data);
        if (data.url) {
          deepLinkInApp(data.url);
        }
      }
    );
  }

  private async autoSignInIfToken() {
    const { token } = getUrlParameters({ url: location.search });
    if (!token) return;
    await signInWithToken(token);
    await waitForAuthLoaded();
    await waitForAuthenticated();
  }

  private async getPartnerDataIfPartnerId() {
    const partnerId = await this.getPartnerId();
    await getAndSetPartnerData(partnerId);
  }

  private async getPartnerId(): Promise<string | null> {
    const params = getUrlParameters({ url: location.search });
    let partnerId: string | undefined | null = params.partnerId;
    if (!partnerId) {
      partnerId = await getStorageKey(StorageKeys.og_partner_id);
    }
    if (partnerId === 'clear') {
      await removeStorageKey(StorageKeys.og_partner_id);
      partnerId = null;
    }
    return partnerId;
  }

  private hideLoading() {
    const loading = document.querySelector('#app-loading');
    loading?.remove();
  }

  private debugStoreLogs() {
    if (environment.env !== 'dev' && !uiState.local) return;
    function logSetStore(store: string, key: string, value: any) {
      console.log(store, 'store changed:', key, value);
    }
    onAuthStore('set', (key, value) => logSetStore('auth', key, value));
    onBudgetStore('set', (key, value) => logSetStore('budget', key, value));
    onGroupStore('set', (key, value) => logSetStore('group', key, value));
    onUiStore('set', (key, value) => logSetStore('ui', key, value));
    onTransactionsStore('set', (key, value) =>
      logSetStore('transaction', key, value)
    );
    onBudgetViewStore('set', (key, value) => logSetStore('views', key, value));
    onRulesStore('set', (key, value) => logSetStore('rules', key, value));
  }

  private async openURL(data: URLOpenListenerEvent) {
    if (!data) return;
    const url = data.url;
    deepLinkInApp(url);
  }

  private setupKeyboard() {
    if (!Capacitor.isNativePlatform()) return;
    Keyboard.addListener('keyboardWillShow', () => (this.keyboardShown = true));
    Keyboard.addListener(
      'keyboardWillHide',
      () => (this.keyboardShown = false)
    );
  }

  private saveAttribution() {
    const params = getUrlParameters({ url: location.search });

    const attribution: Partial<SignUpAttribution> = {
      invited_by_uid: params.invited_by_uid,
      og_utm_campaign: params.utm_campaign,
      og_utm_content: params.utm_content,
      og_utm_medium: params.utm_medium,
      og_utm_source: params.utm_source,
    };
    saveAttributionIfNotPresent(attribution);
  }

  render() {
    const demo = uiState.show_demo_banner;
    const deleteAccount =
      groupState &&
      groupState.groupDoc.flagged_for_deletion_on &&
      groupState.groupDoc.flagged_for_deletion_on > '';
    return (
      <Fragment>
        {demo && (
          <div class="top-banner demo-banner" onClick={() => groupDemoExit()}>
            <div>{translate('EXIT_DEMO')}</div>
          </div>
        )}
        {deleteAccount && (
          <div
            class="top-banner delete-account-banner"
            onClick={() => openMessenger('delete-banner')}
          >
            <div>{translate('DELETE_ACCOUNT')}</div>
          </div>
        )}
        <ion-app
          style={{ top: demo || deleteAccount ? '40px' : undefined }}
          class={{ 'og-keyboard-shown': this.keyboardShown }}
        >
          {renderRouter(authState, uiState.features)}
          <ion-router-outlet />
          {groupState.groupDoc.group_id &&
            groupState.groupDoc.unread_messages > 0 &&
            !uiState.snooze_chat && (
              <og-chat-prompt
                conversationId={groupState.groupDoc.group_id}
                unreadMessages={groupState.groupDoc.unread_messages}
              />
            )}
        </ion-app>
      </Fragment>
    );
  }
}
